/**
 * GetAvailableValues Endpoint
 *
 * Fetches available filter values from the Player API (destinations, countries, regions, hotels, etc.)
 * This endpoint is typically called once and cached for filter population.
 */

import type { GetAvailableValuesResult } from '@/types/schemas';
import { playerClient } from './playerClient';
import { normalizeAvailableValuesResult } from '@/services/mappers/arrayNormalizers';
import { buildRequestHeader } from '@/utils/xml/xmlBuilder';
import { PLAYER_API_CONFIG } from '@/constants/player/config';

const { NAMESPACE } = PLAYER_API_CONFIG;

function createGetAvailableValuesRequest(authKey: string): string {
  return `
<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:bewo="${NAMESPACE}">
   <soapenv:Header/>
   <soapenv:Body>
      <bewo:GetAvailableValues>
         <bewo:GetAvailableValuesRequest>
            ${buildRequestHeader(authKey, 'Package')}
            <bewo:CriteriaTypes>
               <bewo:CriteriaType>All</bewo:CriteriaType>
            </bewo:CriteriaTypes>
         </bewo:GetAvailableValuesRequest>
      </bewo:GetAvailableValues>
   </soapenv:Body>
</soapenv:Envelope>`;
}

/**
 * Calls GetAvailableValues API endpoint
 *
 * Fetches all available filter values (countries, regions, destinations, hotels, etc.)
 * The result is automatically normalized to ensure all arrays are type-safe.
 *
 * @param options - Optional configuration (signal for request cancellation)
 * @returns Object containing raw XML response and normalized result
 * @throws {PlayerApiError} On API errors or invalid responses
 *
 * @example
 * ```typescript
 * const { result } = await getAvailableValues();
 * if (result) {
 *   const countries = result.Countries?.Country || [];
 *   const destinations = result.Destinations?.Destination || [];
 * }
 * ```
 *
 * @example With request cancellation
 * ```typescript
 * const controller = new AbortController();
 * const { result } = await getAvailableValues({
 *   signal: controller.signal
 * });
 * // controller.abort() to cancel request
 * ```
 */
export async function getAvailableValues(options?: { signal?: AbortSignal }): Promise<{
  raw: string;
  result: GetAvailableValuesResult | null;
}> {
  const requestBody = createGetAvailableValuesRequest('');

  const response = await playerClient.call({
    action: 'BewotecHubService/GetAvailableValues',
    body: requestBody,
    signal: options?.signal,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = response.parsed as any;
  const rawResult =
    parsed?.Envelope?.Body?.GetAvailableValuesResponse?.GetAvailableValuesResult || null;

  const result = rawResult
    ? normalizeAvailableValuesResult(rawResult as GetAvailableValuesResult)
    : null;

  return { raw: response.raw, result };
}
